
import type { DataTablePaginationState } from '@neo/blocks/cx-data-table'
import { createStore } from '@shared/utils/state/state-factory'
import { TaskViewName } from 'v2/types'
import type {
  FiltersType,
  IFilterChild,
  IFilterGroup,
  ViewRecordsWithPagination,
} from 'v2/types/api/conversations'

interface TaskStore {
  currentView: TaskViewName
}

interface Actions {
  setCurrentView: (view: TaskViewName) => void
  viewFilters: Record<string, ViewRecordsWithPagination | never>
  setViewTableViewParams: (taskViewName: string, viewParams?: ViewRecordsWithPagination) => void
  setViewTablePagination: (taskViewName: string, pagination?: DataTablePaginationState) => void
  setViewTableSearchQuery: (taskViewName: string, searchQuery?: string) => void
  setViewTableFilters: (taskViewName: string, filters?: FiltersType | Record<string, never>) => void
  setViewTableColumns: (taskViewName: string, fields?: string[]) => void
}

export const defaultFilterChild: IFilterChild = {
  op_type: '',
  type: '',
  value: '',
}

export const initialFilterState = {
  filters: {
    filter_groups: [
      {
        filter_children: [defaultFilterChild],
        filter_conditions: [],
        group_child: false,
      },
    ] as IFilterGroup[],
    group_conditions: [],
  },
}

export const useTaskStore = createStore<TaskStore & Actions>(
  'task',
  (set) => ({
    viewFilters: {},
    currentView: TaskViewName.AllTasks,
    setCurrentView: (view) =>
      set((state) => {
        state.currentView = view
      }),
    setViewTableViewParams: (taskViewName, filter) =>
      set((state) => {
        if (filter && taskViewName) {
          state.viewFilters[taskViewName] = filter
        }
      }),
    setViewTablePagination: (taskViewName, pagination) => {
      set((state) => {
        if (taskViewName) {
          const filter = state.viewFilters[taskViewName]
          if (filter && pagination) {
            filter.pagination = pagination
          }
        }
      })
    },
    setViewTableSearchQuery: (taskViewName, searchQuery) => {
      set((state) => {
        if (taskViewName && state.viewFilters[taskViewName]) {
          state.viewFilters[taskViewName].search_query = searchQuery ?? ''
        }
      })
    },
    setViewTableFilters: (taskViewName, filters = {}) => {
      set((state) => {
        if (taskViewName && state.viewFilters[taskViewName] && filters) {
          state.viewFilters[taskViewName].filters = filters
        }
      })
    },
    setViewTableColumns: (taskViewName, fields) => {
      set((state) => {
        if (taskViewName && state.viewFilters[taskViewName] && fields) {
          state.viewFilters[taskViewName].fields = fields
        }
      })
    },
  }),
  {
    persist: true,
  },
)
